import { v7 as uuidv7 } from 'uuid';
import { getCsrfToken, requestHeaders } from './helpers/csrf';
import { readResponsePayload, resolveErrorMessage } from './helpers/error-handler';

/**
 * BaseUiNode — Núcleo compartilhado entre páginas e componentes da nova área.
 *
 * Responsabilidades:
 *   - config, componentId, root, _destroyed
 *   - uid() — gerador de IDs únicos (UUID v7)
 *   - services (FlyServices) e notification (showSuccess, showError, etc.)
 *   - ciclo de vida: onInit → bindEvents → afterViewInit, onDestroy
 *   - helpers de DOM: $() e $$() com escopo no root
 *   - mount() e destroy()
 *
 * Não deve ser instanciada diretamente — use Page ou Component.
 */
class BaseUiNode {

    /**
     * Gera um ID único com UUID v7.
     *
     * @param {string} [prefix='nd']  Prefixo do ID gerado.
     * @returns {string}  Ex: 'nd-0195fbf4-80d7-79a9-b19f-5d8f0b4d0fb4'
     */
    static uid(prefix = 'nd') {
        return `${prefix}-${uuidv7()}`;
    }

    /**
     * @param {Object} [config={}]
     * @param {string} [config.componentId]  ID lógico do nó (ex: 'certificados-create').
     * @param {string} [config.selector]     Seletor CSS para resolver o root element.
     * @param {Element} [config.root]        Elemento root explícito (tem prioridade sobre selector).
     */
    constructor(config = {}) {
        this.config      = config;
        this.componentId = config.componentId ?? BaseUiNode.uid('nd');
        this._destroyed  = false;

        // ── Resolve root ──────────────────────────────────────────────
        // Subclasses (Page, Component) podem sobrescrever _resolveRoot()
        // para definir fallback ou exigência de root.
        this.root = this._resolveRoot(config);

        // ── Services ──────────────────────────────────────────────────
        const flyServices = globalThis.FlyServices ?? {};
        this.services = {
            overlay: flyServices.overlay,
            dialog:  flyServices.dialog,
            aviso:   flyServices.aviso,
            alert:   flyServices.alert,
            notify:  flyServices.notify,
            loading: flyServices.loading,
        };

        // ── Notification helpers ──────────────────────────────────────
        this.notification = {
            showSuccess: (title, text = null, htmlText = null, cfg = {}) =>
                this._showToast('success', title, text, htmlText, cfg),
            showError: (title, text = null, htmlText = null, cfg = {}) =>
                this._showToast('error', title, text, htmlText, cfg),
            showWarning: (title, text = null, htmlText = null, cfg = {}) =>
                this._showToast('warning', title, text, htmlText, cfg),
            showInfo: (title, text = null, htmlText = null, cfg = {}) =>
                this._showToast('info', title, text, htmlText, cfg),
            confirm: (title, text, htmlText = null,
                      confirmButtonText = 'Sim, quero continuar',
                      cancelButtonText  = 'Não, cancelar',
                      type              = 'primary') =>
                this._confirm(title, text, htmlText, confirmButtonText, cancelButtonText, type),
            showLoading: (title = 'Atualizando informações...') =>
                this._showLoading(title),
            hideLoading: () =>
                this._hideLoading(),
        };
    }

    // ─── Lifecycle hooks — sobrescreva nas subclasses ─────────────────

    /** Setup de estado, instâncias, dados iniciais. */
    onInit() {}

    /** Registro de ouvintes de eventos no DOM. */
    bindEvents() {}

    /** DOM pronto e eventos registrados; operações dependentes de render. */
    afterViewInit() {}

    /** Cleanup: remover listeners, cancelar timers, limpar estado. */
    onDestroy() {}

    // ─── API pública ──────────────────────────────────────────────────

    /**
     * Executa o ciclo de vida: onInit → bindEvents → afterViewInit.
     */
    mount() {
        if (this._destroyed) return;
        this.onInit();
        this.bindEvents();
        this.afterViewInit();
    }

    /**
     * Destrói a instância, executando onDestroy().
     */
    destroy() {
        if (this._destroyed) return;
        this._destroyed = true;
        this.onDestroy();
    }

    /**
     * querySelector com escopo no root.
     * @param {string} selector
     * @returns {Element|null}
     */
    $(selector) {
        return this.root.querySelector(selector);
    }

    /**
     * querySelectorAll com escopo no root.
     * @param {string} selector
     * @returns {NodeList}
     */
    $$(selector) {
        return this.root.querySelectorAll(selector);
    }

    // ─── Resolução de root (sobrescrevível) ───────────────────────────

    /**
     * Resolve o elemento root a partir da config.
     * Subclasses sobrescrevem para definir fallback ou exigência.
     *
     * @param {Object} config
     * @returns {Element}
     */
    _resolveRoot(config) {
        if (config.root instanceof Element) return config.root;
        if (config.selector) return document.querySelector(config.selector) ?? document.body;
        return document.body;
    }

    // ─── Privado: notification helpers ────────────────────────────────

    _showToast(type, title, text, htmlText, config) {
        const notify = this.services.notify;
        if (!notify) return null;

        const messageBody = htmlText ?? text ?? '';
        const message = messageBody
            ? `<strong>${title}</strong><br>${messageBody}`
            : `${title}`;

        return notify.show({
            variant:     config.type ?? type,
            title:       config.title ?? '',
            message,
            duration:    config.duration ?? 4000,
            placement:   config.placement ?? 'top-right',
            dismissible: config.dismissible ?? true,
        });
    }

    _confirm(title, text, htmlText, confirmButtonText, cancelButtonText, type = 'primary') {
        const alert = this.services.alert;

        if (!alert) {
            const plain = [title, htmlText ?? text].filter(Boolean).join('\n\n');
            return Promise.resolve(globalThis.confirm(plain));
        }

        const content = htmlText ?? text ?? '';
        return alert.show({
            type,
            title,
            message:     content,
            confirmText: confirmButtonText,
            cancelText:  cancelButtonText,
            showCancel:  true,
        });
    }

    _showLoading(title) {
        const loading = this.services.loading;
        if (!loading) return;
        loading.show({
            message:        title || 'Atualizando informações...',
            overlayZIndex:  999997,
            containerZIndex: 999998,
        });
    }

    _hideLoading() {
        const loading = this.services.loading;
        if (!loading) return;
        loading.hide();
    }

    _csrfToken() {
        return getCsrfToken();
    }

    _requestHeaders(headers = {}) {
        return requestHeaders(headers);
    }

    async _readResponsePayload(response) {
        return readResponsePayload(response);
    }

    _resolveErrorMessage(error, fallback = 'Ocorreu um erro inesperado.') {
        return resolveErrorMessage(error, fallback);
    }

    async _submitForm(options = {}) {
        const {
            form,
            submitButton = null,
            method = 'POST',
            headers = {},
            body = null,
            loadingMessage = 'Salvando...',
            redirectUrl = null,
            successHandler = null,
            errorTitle = 'Erro',
            defaultErrorMessage = 'Não foi possível concluir a operação.',
        } = options;

        if (!form) return;

        if (submitButton) submitButton.disabled = true;
        this._clearDirty?.();
        this.notification.showLoading(loadingMessage);

        try {
            const response = await fetch(form.action, {
                method,
                headers: this._requestHeaders(headers),
                body: body ?? new FormData(form),
            });

            if (response.ok) {
                const payload = await this._readResponsePayload(response);
                if (typeof successHandler === 'function') {
                    await successHandler({ response, payload });
                } else if (redirectUrl) {
                    globalThis.location.href = redirectUrl;
                }
                return;
            }

            const payload = await this._readResponsePayload(response);
            throw payload ?? defaultErrorMessage;
        } catch (error) {
            this.notification.hideLoading();
            if (submitButton) submitButton.disabled = false;
            this.notification.showError(errorTitle, this._resolveErrorMessage(error, defaultErrorMessage));
        }
    }
}

export default BaseUiNode;
